// src/views/Contact.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PublicTopBar from "./PublicTopBar";
import Footer from "../components/Footer";
import { publicSiteWebService } from "../services/publicSiteWebService";

export default function Contact() {
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState({
    parametres: null,
    contact: null,
  });
  
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    etablissement: "",
    message: "",
    sujet: "information"
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await publicSiteWebService.getPublicData();
        setPageData({
          parametres: data.parametres,
          contact: data.contact,
        });
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const params = pageData.parametres || {};
  const contactInfo = pageData.contact || {};
  const primaryColor = params.couleur_primaire || "#004080";
  const secondaryColor = params.couleur_secondaire || "#0066CC";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.prenom.trim()) newErrors.prenom = "Le prénom est requis";
    if (!formData.nom.trim()) newErrors.nom = "Le nom est requis";
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email invalide";
    }
    if (!formData.message.trim()) newErrors.message = "Le message est requis";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await publicSiteWebService.submitContact(formData);
      setIsSubmitted(true);
      setFormData({
        prenom: "",
        nom: "",
        email: "",
        telephone: "",
        etablissement: "",
        message: "",
        sujet: "information"
      });
    } catch (error) {
      let errorMessage = "Une erreur est survenue lors de l'envoi";
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          const errors = Object.values(error.response.data).flat();
          errorMessage = errors.join("\n");
        } else {
          errorMessage = error.response.data.error || error.response.data.detail || errorMessage;
        }
      }
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: "ti-clock",
      title: "Réponse rapide",
      desc: "Nous vous répondons sous 24h ouvrées"
    },
    {
      icon: "ti-headset",
      title: "Support dédié",
      desc: "Une équipe à votre écoute pour vous accompagner"
    },
    {
      icon: "ti-lock",
      title: "Données sécurisées",
      desc: "Vos informations sont protégées et confidentielles"
    }
  ];

  if (loading) {
    return (
      <div className="contact-page">
        <PublicTopBar />
        <div className="loader-container" style={{ minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div className="loader-spinner" style={{ width: 48, height: 48, border: "4px solid #e2e8f0", borderTopColor: primaryColor, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <PublicTopBar />

      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-bg" />
        <div className="contact-hero-container">
          <div className="contact-hero-content">
            <span className="contact-hero-badge">
              <i className="ti ti-message-circle" />
              Contactez-nous
            </span>
            <h1 className="contact-hero-title">
              Commençons ensemble<br />
              <span className="contact-hero-accent" style={{ color: primaryColor }}>
                votre projet
              </span>
            </h1>
            <p className="contact-hero-desc">
              Remplissez le formulaire ci-dessous et notre équipe vous contactera
              pour discuter de vos besoins et vous proposer une solution sur mesure.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-section">
        <div className="contact-container">
          <div className="contact-grid">
            {/* Formulaire */}
            <div className="contact-form-wrapper">
              <div className="contact-form-card">
                <div className="contact-form-header">
                  <h2 className="contact-form-title">Envoyez-nous un message</h2>
                  <p className="contact-form-subtitle">
                    Tous les champs marqués d'un <span className="required-star">*</span> sont obligatoires
                  </p>
                </div>

                {isSubmitted ? (
                  <div className="contact-success">
                    <div className="contact-success-icon" style={{ background: primaryColor }}>
                      <i className="ti ti-check" />
                    </div>
                    <h3>Message envoyé !</h3>
                    <p>
                      Merci {formData.prenom} ! Notre équipe vous contactera dans les plus brefs délais.
                    </p>
                    <button 
                      onClick={() => setIsSubmitted(false)} 
                      className="btn btn-primary"
                      style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                    >
                      <i className="ti ti-arrow-left" />
                      Envoyer un nouveau message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="contact-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="prenom">
                          Prénom <span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="prenom"
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleChange}
                          placeholder="Jean"
                          className={errors.prenom ? "error" : ""}
                        />
                        {errors.prenom && <span className="form-error">{errors.prenom}</span>}
                      </div>
                      <div className="form-group">
                        <label htmlFor="nom">
                          Nom <span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="nom"
                          name="nom"
                          value={formData.nom}
                          onChange={handleChange}
                          placeholder="Dupont"
                          className={errors.nom ? "error" : ""}
                        />
                        {errors.nom && <span className="form-error">{errors.nom}</span>}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="email">
                          Email <span className="required-star">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="jean.dupont@email.com"
                          className={errors.email ? "error" : ""}
                        />
                        {errors.email && <span className="form-error">{errors.email}</span>}
                      </div>
                      <div className="form-group">
                        <label htmlFor="telephone">Téléphone</label>
                        <input
                          type="tel"
                          id="telephone"
                          name="telephone"
                          value={formData.telephone}
                          onChange={handleChange}
                          placeholder="+226 XX XX XX XX"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="etablissement">Établissement</label>
                      <input
                        type="text"
                        id="etablissement"
                        name="etablissement"
                        value={formData.etablissement}
                        onChange={handleChange}
                        placeholder="Nom de votre école ou établissement"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="sujet">Sujet</label>
                      <select
                        id="sujet"
                        name="sujet"
                        value={formData.sujet}
                        onChange={handleChange}
                      >
                        <option value="information">Demande d'information</option>
                        <option value="demo">Demande de démo</option>
                        <option value="pricing">Devis / Tarifs</option>
                        <option value="support">Support technique</option>
                        <option value="partnership">Partenariat</option>
                        <option value="other">Autre</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message">
                        Message <span className="required-star">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows="5"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Décrivez votre projet, vos besoins ou votre demande..."
                        className={errors.message ? "error" : ""}
                      />
                      {errors.message && <span className="form-error">{errors.message}</span>}
                    </div>

                    <div className="form-group form-checkbox">
                      <label className="checkbox-label">
                        <input type="checkbox" required />
                        <span className="checkmark" />
                        J'accepte que mes données soient utilisées pour me recontacter
                      </label>
                    </div>

                    <button 
                      type="submit" 
                      className="btn btn-primary btn-submit"
                      disabled={isSubmitting}
                      style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-send" />
                          Envoyer le message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Sidebar - Infos */}
            <div className="contact-sidebar">
              <div className="contact-sidebar-card">
                <h3 className="contact-sidebar-title">Pourquoi nous contacter ?</h3>
                <div className="contact-benefits">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="contact-benefit">
                      <div className="contact-benefit-icon" style={{ color: primaryColor, background: `${primaryColor}12` }}>
                        <i className={`ti ${benefit.icon}`} />
                      </div>
                      <div>
                        <h4>{benefit.title}</h4>
                        <p>{benefit.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <hr className="contact-divider" />

                <div className="contact-info">
                  <h4>Informations de contact</h4>
                  <div className="contact-info-item">
                    <i className="ti ti-mail" style={{ color: primaryColor }} />
                    <div>
                      <span>Email</span>
                      <a href={`mailto:${contactInfo.email || 'contact@enumera.com'}`}>
                        {contactInfo.email || 'contact@enumera.com'}
                      </a>
                    </div>
                  </div>
                  {contactInfo.telephone && (
                    <div className="contact-info-item">
                      <i className="ti ti-phone" style={{ color: primaryColor }} />
                      <div>
                        <span>Téléphone</span>
                        <a href={`tel:${contactInfo.telephone}`}>{contactInfo.telephone}</a>
                      </div>
                    </div>
                  )}
                  {contactInfo.horaires && (
                    <div className="contact-info-item">
                      <i className="ti ti-clock" style={{ color: primaryColor }} />
                      <div>
                        <span>Horaires</span>
                        <span>{contactInfo.horaires}</span>
                      </div>
                    </div>
                  )}
                  {contactInfo.adresse && (
                    <div className="contact-info-item">
                      <i className="ti ti-location" style={{ color: primaryColor }} />
                      <div>
                        <span>Adresse</span>
                        <span>{contactInfo.adresse}</span>
                      </div>
                    </div>
                  )}
                </div>

                <hr className="contact-divider" />

                <div className="contact-social">
                  <h4>Suivez-nous</h4>
                  <div className="contact-social-links">
                    {contactInfo.linkedin_url && (
                      <a href={contactInfo.linkedin_url} target="_blank" rel="noopener noreferrer" className="contact-social-link">
                        <i className="ti ti-brand-linkedin" />
                      </a>
                    )}
                    {contactInfo.twitter_url && (
                      <a href={contactInfo.twitter_url} target="_blank" rel="noopener noreferrer" className="contact-social-link">
                        <i className="ti ti-brand-twitter" />
                      </a>
                    )}
                    {contactInfo.youtube_url && (
                      <a href={contactInfo.youtube_url} target="_blank" rel="noopener noreferrer" className="contact-social-link">
                        <i className="ti ti-brand-youtube" />
                      </a>
                    )}
                    {contactInfo.facebook_url && (
                      <a href={contactInfo.facebook_url} target="_blank" rel="noopener noreferrer" className="contact-social-link">
                        <i className="ti ti-brand-facebook" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css');

        .contact-page {
          min-height: 100vh;
          background: #f8fafc;
          color: #0f172a;
        }

        /* === HERO SECTION === */
        .contact-hero {
          position: relative;
          padding: 120px 32px 80px;
          background: linear-gradient(135deg, #f8fafc 0%, #e6eef7 100%);
          overflow: hidden;
        }

        .contact-hero-bg {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse at 30% 50%, rgba(0, 64, 128, 0.05) 0%, transparent 60%),
            radial-gradient(ellipse at 70% 50%, rgba(0, 102, 204, 0.03) 0%, transparent 60%);
          pointer-events: none;
        }

        .contact-hero-container {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 0 auto;
        }

        .contact-hero-content {
          text-align: center;
        }

        .contact-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          background: rgba(0, 64, 128, 0.08);
          border: 1px solid rgba(0, 64, 128, 0.15);
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
          color: #004080;
          margin-bottom: 16px;
        }

        .contact-hero-title {
          font-size: 48px;
          font-weight: 800;
          line-height: 1.1;
          margin: 0 0 16px;
          letter-spacing: -0.02em;
          color: #0f172a;
        }

        .contact-hero-accent {
          color: #004080;
        }

        .contact-hero-desc {
          font-size: 18px;
          color: #64748b;
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
        }

        /* === CONTACT SECTION === */
        .contact-section {
          padding: 60px 32px 80px;
        }

        .contact-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .contact-grid {
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: 40px;
          align-items: start;
        }

        /* --- Form --- */
        .contact-form-wrapper {
          background: #ffffff;
          border-radius: 20px;
          border: 1px solid #e2e8f0;
          padding: 40px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
        }

        .contact-form-header {
          margin-bottom: 32px;
        }

        .contact-form-title {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 4px;
          color: #0f172a;
        }

        .contact-form-subtitle {
          font-size: 14px;
          color: #94a3b8;
          margin: 0;
        }

        .required-star {
          color: #ef4444;
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .form-group label {
          font-size: 13px;
          font-weight: 600;
          color: #0f172a;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 10px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 14px;
          font-family: inherit;
          transition: all 0.2s ease;
          background: #f8fafc;
          color: #0f172a;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #004080;
          box-shadow: 0 0 0 3px rgba(0, 64, 128, 0.08);
          background: #ffffff;
        }

        .form-group input.error,
        .form-group textarea.error {
          border-color: #ef4444;
        }

        .form-group input.error:focus,
        .form-group textarea.error:focus {
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.08);
        }

        .form-error {
          font-size: 12px;
          color: #ef4444;
          margin-top: 2px;
        }

        .form-group textarea {
          resize: vertical;
          min-height: 120px;
        }

        .form-checkbox {
          margin-top: 4px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          color: #64748b;
          cursor: pointer;
        }

        .checkbox-label input {
          display: none;
        }

        .checkmark {
          width: 18px;
          height: 18px;
          border: 2px solid #d1d5db;
          border-radius: 4px;
          flex-shrink: 0;
          transition: all 0.2s ease;
          position: relative;
        }

        .checkbox-label input:checked + .checkmark {
          background: #004080;
          border-color: #004080;
        }

        .checkbox-label input:checked + .checkmark::after {
          content: '✓';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #ffffff;
          font-size: 12px;
          font-weight: 700;
        }

        .btn-submit {
          width: 100%;
          justify-content: center;
          padding: 14px;
          font-size: 15px;
          margin-top: 8px;
        }

        .btn-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* --- Success --- */
        .contact-success {
          text-align: center;
          padding: 20px 0;
        }

        .contact-success-icon {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
        }

        .contact-success-icon i {
          font-size: 32px;
          color: #ffffff;
        }

        .contact-success h3 {
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 8px;
          color: #0f172a;
        }

        .contact-success p {
          color: #64748b;
          margin: 0 0 24px;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 15px;
          text-decoration: none;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
        }

        .btn-primary {
          background: linear-gradient(135deg, #004080, #0066CC);
          color: #ffffff;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0, 64, 128, 0.3);
        }

        /* --- Sidebar --- */
        .contact-sidebar-card {
          background: #ffffff;
          border-radius: 20px;
          border: 1px solid #e2e8f0;
          padding: 32px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
        }

        .contact-sidebar-title {
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 24px;
          color: #0f172a;
        }

        .contact-benefits {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .contact-benefit {
          display: flex;
          gap: 14px;
          align-items: flex-start;
        }

        .contact-benefit-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: rgba(0, 64, 128, 0.06);
          color: #004080;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }

        .contact-benefit h4 {
          font-size: 14px;
          font-weight: 600;
          margin: 0 0 2px;
          color: #0f172a;
        }

        .contact-benefit p {
          font-size: 13px;
          color: #94a3b8;
          margin: 0;
        }

        .contact-divider {
          border: none;
          border-top: 1px solid #e2e8f0;
          margin: 24px 0;
        }

        .contact-info h4,
        .contact-social h4 {
          font-size: 14px;
          font-weight: 600;
          margin: 0 0 12px;
          color: #0f172a;
        }

        .contact-info-item {
          display: flex;
          gap: 12px;
          align-items: center;
          padding: 8px 0;
        }

        .contact-info-item i {
          font-size: 18px;
          width: 24px;
        }

        .contact-info-item div {
          display: flex;
          flex-direction: column;
        }

        .contact-info-item span {
          font-size: 12px;
          color: #94a3b8;
        }

        .contact-info-item a,
        .contact-info-item span:last-child {
          font-size: 14px;
          color: #0f172a;
          text-decoration: none;
        }

        .contact-info-item a:hover {
          color: #004080;
        }

        .contact-social-links {
          display: flex;
          gap: 10px;
        }

        .contact-social-link {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          font-size: 18px;
          transition: all 0.2s ease;
          text-decoration: none;
        }

        .contact-social-link:hover {
          background: #004080;
          border-color: #004080;
          color: #ffffff;
        }

        /* === RESPONSIVE === */
        @media (max-width: 1024px) {
          .contact-grid {
            grid-template-columns: 1fr;
          }

          .contact-hero-title {
            font-size: 36px;
          }
        }

        @media (max-width: 768px) {
          .contact-hero {
            padding: 100px 20px 60px;
          }

          .contact-hero-title {
            font-size: 30px;
          }

          .contact-section {
            padding: 40px 20px 60px;
          }

          .contact-form-wrapper {
            padding: 24px;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .contact-sidebar-card {
            padding: 24px;
          }
        }

        @media (max-width: 480px) {
          .contact-hero-title {
            font-size: 24px;
          }

          .contact-hero-desc {
            font-size: 15px;
          }

          .contact-form-title {
            font-size: 20px;
          }

          .contact-form-wrapper {
            padding: 16px;
          }
        }
      `}</style>
    </div>
  );
}